import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, NgZone, AfterViewInit, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import {
  fireVendorCompleteRegistration,
  prepareVendorRegistrationCapi,
} from 'src/app/shared/meta-pixel';
import {
  AnalyticsService,
  GA4_EVENTS,
  VENDOR_EVENTS,
} from 'src/app/shared/analytics';
import { analyticsDistinctId, rawIdFromUserInfo } from 'src/app/shared/analytics/identity';
import { LegalDocKey, legalConsentPayload } from 'src/app/shared/legal/legal-documents';

declare var google: any;

type SignUpStep = 'identifier' | 'otp' | 'details';

@Component({
  selector: 'app-vendor-register',
  templateUrl: './vendor-register.component.html',
  styleUrl: './vendor-register.component.scss',
})
export class VendorRegisterComponent implements AfterViewInit, OnDestroy {
  currentStep: SignUpStep = 'identifier';

  // Identifier (email or phone)
  identifier = '';
  identifierError = '';
  isLoading = false;

  // OTP
  otpCode = '';
  otpError = '';
  maskedIdentifier = '';
  otpChannel: 'email' | 'sms' = 'email';
  otpTimer = 0;
  private otpInterval: any;

  // Details (Step 3)
  fullName = '';
  accountType: 'individual' | 'agency' = 'individual';
  agencyName = '';
  secondaryContact = '';
  secondaryContactError = '';
  phoneExistsError = false;

  // Terms of Use + Privacy Policy consent
  agreedToTerms = false;
  legalModal: LegalDocKey | null = null;

  // Google signup
  isGoogleSignup = false;
  googleData: any = null;

  // Help desk
  showHelpModal = false;

  private readonly GOOGLE_CLIENT_ID = '46261176784-bq1i72v4laj1egabvm3r1mf5eq7kp9dn.apps.googleusercontent.com';
  private vendorBaseUri = environment.vendorBaseUri;

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private http: HttpClient,
    private ngZone: NgZone,
    private analytics: AnalyticsService,
  ) {}

  ngAfterViewInit(): void {
    this.initializeGoogleSignIn();
  }

  ngOnDestroy(): void {
    this.clearOtpTimer();
  }

  // ─── Helpers ───

  get isEmail(): boolean {
    return this.identifier.includes('@');
  }

  get isValidIdentifier(): boolean {
    const val = this.identifier.trim();
    if (!val) return false;
    if (val.includes('@')) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    }
    return /^[6-9]\d{9}$/.test(val.replace(/\D/g, ''));
  }

  get identifierPlaceholder(): string {
    return 'Enter email or phone number';
  }

  get isValidDetails(): boolean {
    if (!this.agreedToTerms) return false;
    if (this.phoneExistsError) return false;
    if (this.accountType === 'individual') {
      if (!this.fullName.trim() || this.fullName.trim().length < 2) return false;
    }
    if (this.accountType === 'agency') {
      if (!this.agencyName.trim() || this.agencyName.trim().length < 2) return false;
    }
    return true;
  }

  get needsPhone(): boolean {
    return this.isEmail || this.isGoogleSignup;
  }

  get needsEmail(): boolean {
    return !this.isEmail && !this.isGoogleSignup;
  }

  get isValidSecondaryContact(): boolean {
    const val = this.secondaryContact.trim();
    if (!val) return false;
    if (this.needsPhone) {
      return /^[6-9]\d{9}$/.test(val.replace(/\D/g, ''));
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  }

  // ─── Google Sign-In ───

  private initializeGoogleSignIn(): void {
    if (typeof google === 'undefined') {
      setTimeout(() => this.initializeGoogleSignIn(), 500);
      return;
    }

    google.accounts.id.initialize({
      client_id: this.GOOGLE_CLIENT_ID,
      callback: (response: any) => this.handleGoogleCredential(response),
    });

    const btnEl = document.getElementById('google-signin-btn');
    if (btnEl) {
      google.accounts.id.renderButton(btnEl, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signup_with',
        width: 380,
        logo_alignment: 'center',
      });
    }
  }

  private handleGoogleCredential(response: any): void {
    this.ngZone.run(() => {
      this.isLoading = true;
      const headers = new HttpHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' });

      this.http
        .post<any>(`${this.vendorBaseUri}/unauth/auth/google-signup`, { token: response.credential }, { headers })
        .subscribe({
          next: (res: any) => {
            this.isLoading = false;

            if (res?.data?.token) {
              // Account already exists, log them in
              this.handleSuccessfulLogin(res);
              return;
            }

            // New Google user — skip to details step
            this.isGoogleSignup = true;
            this.googleData = res.data;
            this.identifier = res.data?.email || '';
            this.fullName = res.data?.name || '';
            this.currentStep = 'details';
            this.snackBar.open('Google account verified. Complete your profile.', 'Close', { duration: 4000 });
          },
          error: (err: any) => {
            this.isLoading = false;
            if (err?.error?.error === 'ACCOUNT_EXISTS') {
              this.snackBar.open('An account with this email already exists. Please sign in.', 'Close', { duration: 5000 });
            } else {
              this.snackBar.open(err?.error?.error || 'Google sign-up failed. Please try again.', 'Close', { duration: 5000 });
            }
          },
        });
    });
  }

  // ─── Step 1: Send Signup OTP ───

  sendOtp(): void {
    const val = this.identifier.trim();
    if (!this.isValidIdentifier) {
      this.identifierError = val.includes('@')
        ? 'Please enter a valid email address.'
        : 'Please enter a valid 10-digit mobile number.';
      return;
    }

    this.isLoading = true;
    this.identifierError = '';

    const headers = new HttpHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' });
    const body = { identifier: val.includes('@') ? val : val.replace(/\D/g, '') };

    this.http
      .post<any>(`${this.vendorBaseUri}/unauth/vendor/send-signup-otp`, body, { headers })
      .subscribe({
        next: (res: any) => {
          this.isLoading = false;
          this.maskedIdentifier = res?.masked || '';
          this.otpChannel = res?.channel || 'email';
          this.currentStep = 'otp';
          this.startOtpTimer();
        },
        error: (err: any) => {
          this.isLoading = false;
          this.identifierError = err?.error?.error || 'Failed to send OTP. Please try again.';
        },
      });
  }

  // ─── Step 2: Verify Signup OTP ───

  verifyOtp(): void {
    if (!this.otpCode || this.otpCode.length !== 6) {
      this.otpError = 'Please enter the 6-digit OTP.';
      return;
    }

    this.isLoading = true;
    this.otpError = '';

    const headers = new HttpHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' });
    const val = this.identifier.trim();
    const body = { identifier: val.includes('@') ? val : val.replace(/\D/g, ''), otp: this.otpCode };

    this.http
      .post<any>(`${this.vendorBaseUri}/unauth/vendor/verify-signup-otp`, body, { headers })
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.currentStep = 'details';
          this.clearOtpTimer();
        },
        error: (err: any) => {
          this.isLoading = false;
          this.otpError = err?.error?.error || 'Invalid OTP. Please try again.';
        },
      });
  }

  // ─── Check phone exists ───

  checkPhoneExists(): void {
    const val = this.secondaryContact.trim().replace(/\D/g, '');
    if (!val || val.length !== 10) {
      this.secondaryContactError = '';
      this.phoneExistsError = false;
      return;
    }

    const headers = new HttpHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' });

    this.http
      .post<any>(`${this.vendorBaseUri}/unauth/vendor/check-phone`, { phone: val }, { headers })
      .subscribe({
        next: (res: any) => {
          if (res?.exists) {
            this.phoneExistsError = true;
            this.secondaryContactError = 'This number is already linked to an account.';
          } else {
            this.phoneExistsError = false;
            this.secondaryContactError = '';
          }
        },
        error: () => {
          this.phoneExistsError = false;
          this.secondaryContactError = '';
        },
      });
  }

  redirectToPhoneLogin(): void {
    this.router.navigateByUrl('/vendors/login');
  }

  // ─── Step 3: Complete Signup ───

  completeSignup(): void {
    if (this.accountType === 'individual'
      && (!this.fullName.trim() || this.fullName.trim().length < 2)) {
      this.snackBar.open('Please enter your full name.', 'Close', { duration: 3000, verticalPosition: 'top' });
      return;
    }
    if (this.accountType === 'agency'
      && (!this.agencyName.trim() || this.agencyName.trim().length < 2)) {
      this.snackBar.open('Please enter your agency name.', 'Close', { duration: 3000, verticalPosition: 'top' });
      return;
    }
    if (!this.agreedToTerms) {
      this.snackBar.open('Please agree to the Terms of Use and Privacy Policy before proceeding.', 'Close', { duration: 4000, verticalPosition: 'top' });
      return;
    }
    if (this.phoneExistsError) {
      this.snackBar.open('The phone number is already linked to another account.', 'Close', { duration: 3000, verticalPosition: 'top' });
      return;
    }

    if (!this.isValidDetails) return;

    this.isLoading = true;

    const headers = new HttpHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' });
    const val = this.identifier.trim();
    const payloadName = this.accountType === 'agency'
      ? this.agencyName.trim()
      : this.fullName.trim();
    const body: any = {
      identifier: val.includes('@') ? val : val.replace(/\D/g, ''),
      name: payloadName,
      account_type_id: this.accountType === 'individual' ? 1 : 2,
      // `is_agree` predates the stored consent record; kept for any backend
      // that still reads it. The versioned fields below are what gets stored.
      is_agree: true,
      ...legalConsentPayload(),
    };

    // Add secondary contact
    const secondary = this.secondaryContact.trim();
    if (secondary) {
      if (this.needsPhone) {
        body.mobile = secondary.replace(/\D/g, '');
      } else {
        body.email = secondary;
      }
    }

    if (this.accountType === 'agency' && this.agencyName.trim()) {
      body.agency_name = this.agencyName.trim();
    }

    if (this.isGoogleSignup && this.googleData) {
      body.login_provider = 'google';
      body.google_id = this.googleData.google_id;
    }

    const capi = prepareVendorRegistrationCapi();
    body.event_id = capi.event_id;
    body.event_source_url = capi.event_source_url;
    body.fbp = capi.fbp;
    body.fbc = capi.fbc;
    const conversionMethod: 'google' | 'email_phone' =
      this.isGoogleSignup ? 'google' : 'email_phone';

    this.http
      .post<any>(`${this.vendorBaseUri}/unauth/vendor/complete-signup`, body, { headers })
      .subscribe({
        next: (res: any) => {
          this.isLoading = false;

          fireVendorCompleteRegistration(conversionMethod, capi.event_id);

          this.analytics.track(GA4_EVENTS.SIGN_UP, {
            method: conversionMethod,
            role: 'vendor',
            event_id: capi.event_id,
          });
          this.analytics.track(VENDOR_EVENTS.VENDOR_REGISTERED, {
            method: conversionMethod,
            account_type: this.accountType,
            has_agency_name: !!this.agencyName?.trim(),
            has_secondary_contact: !!this.secondaryContact?.trim(),
            event_id: capi.event_id,
          });

          if (res?.data?.token) {
            this.handleSuccessfulLogin(res);
          } else {
            this.snackBar.open('Account created! Please sign in.', 'Close', { duration: 4000 });
            this.router.navigateByUrl('/vendors/login');
          }
        },
        error: (err: any) => {
          this.isLoading = false;
          const msg = err?.error?.error || err?.error?.message || 'Registration failed. Please try again.';
          this.snackBar.open(msg, 'Close', { duration: 5000 });
          this.analytics.track(VENDOR_EVENTS.VENDOR_LOGIN_FAILED, {
            stage: 'complete_signup',
            error_message: msg,
          });
        },
      });
  }

  // ─── Login success ───

  private handleSuccessfulLogin(response: any): void {
    localStorage.setItem('accessToken', response.data.token);
    localStorage.setItem('tokenType', response.data.token_type || 'bearer');
    localStorage.setItem(
      'tokenExpiry',
      (Date.now() + (response.data.expires_in || 3600) * 1000 * 60).toString()
    );
    localStorage.setItem('profilePicture', response.data.profile_picture || '');

    const userInfo = {
      sub: response.data.id || response.data.sub || '',
      'custom:team': response.data.contributor_code || '',
      'custom:visibility': 'vendor',
      email: response.data.email || '',
      name: response.data.name || this.fullName || '',
      phone_number: response.data.phone || '',
      phone_number_verified: false,
      token_use: 'id',
      auth_type: this.isGoogleSignup ? 'google' : 'email',
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 60 * 60,
    };
    localStorage.setItem('userInfo', JSON.stringify(userInfo));

    const distinctId = analyticsDistinctId('vendor', rawIdFromUserInfo('vendor', userInfo));
    this.analytics.identify({
      distinctId: distinctId ?? '',
      role: 'vendor',
      properties: {
        email: userInfo.email,
        name: userInfo.name,
        phone: userInfo.phone_number,
        contributor_code: userInfo['custom:team'],
        contributor_id: String(userInfo.sub),
        account_type: this.accountType,
      },
    });

    this.router.navigateByUrl('/workspace/vendor/profile').then(() => {
      this.snackBar.open('Welcome aboard!', 'Close', {
        duration: 3000,
        verticalPosition: 'top',
        panelClass: ['success-snackbar'],
      });
    });
  }

  // ─── Navigation ───

  resendOtp(): void {
    if (this.otpTimer > 0) return;
    this.otpCode = '';
    this.otpError = '';
    this.sendOtp();
  }

  goBackToIdentifier(): void {
    this.currentStep = 'identifier';
    this.otpCode = '';
    this.otpError = '';
    this.clearOtpTimer();
  }

  goBackToOtp(): void {
    this.currentStep = 'otp';
  }

  setAccountType(type: 'individual' | 'agency'): void {
    this.accountType = type;
    if (type === 'individual') {
      this.agencyName = '';
    }
  }

  // ─── OTP Input formatting ───

  formatOtpInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\D/g, '').slice(0, 6);
    this.otpCode = input.value;
  }

  // ─── Timer ───

  private startOtpTimer(): void {
    this.clearOtpTimer();
    this.otpTimer = 60;
    this.otpInterval = setInterval(() => {
      this.otpTimer--;
      if (this.otpTimer <= 0) this.clearOtpTimer();
    }, 1000);
  }

  private clearOtpTimer(): void {
    if (this.otpInterval) {
      clearInterval(this.otpInterval);
      this.otpInterval = null;
    }
    this.otpTimer = 0;
  }
}
